import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { computeFileContentHash } from '@/utils/file-content-hash'
import { throwIfVideoUploadDuplicate } from '@/utils/video-upload-duplicate-error'

import { baseService } from './base.service'
import { IBaseService } from './interfaces/base-service.interface'

export class UploadService {
  private static instance: UploadService
  private readonly api: AxiosInstance
  private readonly CHUNK_SIZE = 5 * 1024 * 1024 // 5MB per chunk

  private constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): UploadService {
    if (!UploadService.instance) {
      UploadService.instance = new UploadService(baseService)
    }
    return UploadService.instance
  }

  /**
   * Split file into chunks
   */
  private splitFileIntoChunks(file: File): Blob[] {
    const chunks: Blob[] = []
    let start = 0

    while (start < file.size) {
      const end = Math.min(start + this.CHUNK_SIZE, file.size)
      chunks.push(file.slice(start, end))
      start = end
    }

    return chunks
  }

  /**
   * Upload one TUS chunk directly to Vimeo's upload_link.
   */
  private async uploadChunkToVimeo(
    uploadLink: string,
    chunk: Blob,
    offset: number,
    signal?: AbortSignal
  ): Promise<number> {
    const response = await fetch(uploadLink, {
      method: 'PATCH',
      headers: {
        Accept: 'application/vnd.vimeo.*+json;version=3.4',
        'Tus-Resumable': '1.0.0',
        'Upload-Offset': String(offset),
        'Content-Type': 'application/offset+octet-stream',
      },
      body: chunk,
      signal,
    })

    if (!response.ok) {
      const body = await response.text().catch(() => '')
      throw new Error(
        `Vimeo TUS upload failed at offset ${offset} (${response.status})${body ? `: ${body}` : ''}`
      )
    }

    const newOffsetHeader = response.headers.get('Upload-Offset')
    const newOffset = newOffsetHeader ? parseInt(newOffsetHeader, 10) : offset + chunk.size
    if (!Number.isFinite(newOffset) || newOffset <= offset) {
      throw new Error(`Invalid Upload-Offset from Vimeo after patch at ${offset}`)
    }
    return newOffset
  }

  /**
   * Course/webinar video: init on our API (gets Vimeo TUS upload_link), upload
   * bytes directly to Vimeo, then finalize on our API.
   */
  private async uploadVideoChunked({
    video,
    uploadType,
    duration,
    courseName,
    contentHash: providedContentHash,
    onProgress,
    signal,
    onInitialized,
  }: {
    video: File
    uploadType: 'webinars' | 'course'
    duration?: number
    courseName?: string
    contentHash?: string
    onProgress: (progress: number) => void
    signal?: AbortSignal
    onInitialized?: (payload: { uploadId: string; videoId?: string; uploadLink?: string }) => void
  }): Promise<{ videoId: string; videoUrl: string }> {
    const chunks = this.splitFileIntoChunks(video)
    const totalChunks = chunks.length

    const endpoint = uploadType === 'course' ? '/upload/file/course/init' : '/upload/file/webinars/init'
    const params = new URLSearchParams()
    if (uploadType === 'course' && courseName) {
      params.append('courseName', courseName)
    }
    params.append('fileName', video.name)
    params.append('fileSize', video.size.toString())
    params.append('totalChunks', totalChunks.toString())
    if (duration !== undefined) {
      params.append('duration', duration.toString())
    }

    const contentHash = providedContentHash ?? (await computeFileContentHash(video))
    params.append('contentHash', contentHash)

    const initUrl = params.toString() ? `${endpoint}?${params.toString()}` : endpoint

    try {
      const { data: initData } = await this.api.post<{
        uploadId: string
        videoId?: string
        uploadLink?: string
      }>(initUrl, {})
      const sessionUploadId = initData.uploadId
      const uploadLink = initData.uploadLink
      onInitialized?.({
        uploadId: sessionUploadId,
        videoId: initData.videoId,
        uploadLink,
      })

      if (!sessionUploadId) {
        throw new Error('Failed to initialize video upload session')
      }

      // Prefer direct-to-Vimeo via TUS upload_link from init.
      if (uploadLink) {
        let offset = 0
        for (let i = 0; i < chunks.length; i++) {
          offset = await this.uploadChunkToVimeo(uploadLink, chunks[i], offset, signal)
          const uploadProgress = Math.min(95, Math.round((offset / video.size) * 100))
          onProgress(uploadProgress)
        }

        if (offset < video.size) {
          throw new Error(`Vimeo upload incomplete: ${offset}/${video.size} bytes`)
        }
      } else {
        // Legacy proxy path when uploadLink is unavailable
        let uploadedChunks = 0
        for (let i = 0; i < chunks.length; i++) {
          const result = await this.uploadChunk(
            chunks[i],
            i,
            totalChunks,
            sessionUploadId,
            uploadType,
            courseName
          )
          uploadedChunks++
          const uploadProgress = Math.min(95, Math.round((uploadedChunks / totalChunks) * 100))
          onProgress(result.progress !== undefined ? Math.min(95, result.progress) : uploadProgress)
        }
      }

      const finalizeEndpoint =
        uploadType === 'course' ? '/upload/file/course/finalize' : '/upload/file/webinars/finalize'
      const { data: finalData } = await this.api.post<{ videoId: string; videoUrl: string }>(
        `${finalizeEndpoint}?uploadId=${sessionUploadId}`,
        {}
      )

      onProgress(100)
      return finalData
    } catch (error: unknown) {
      throwIfVideoUploadDuplicate(error)
    }
  }

  /**
   * @deprecated Prefer direct Vimeo TUS upload via uploadLink from init.
   * Kept as fallback when uploadLink is unavailable.
   */
  private async uploadChunk(
    chunk: Blob,
    chunkIndex: number,
    totalChunks: number,
    uploadId: string,
    uploadType: 'webinars' | 'course',
    courseName?: string
  ): Promise<{ progress: number }> {
    const formData = new FormData()
    formData.append('chunk', chunk)
    formData.append('chunkIndex', chunkIndex.toString())
    formData.append('totalChunks', totalChunks.toString())
    formData.append('uploadId', uploadId)

    const endpoint = uploadType === 'course' ? '/upload/file/course/chunk' : '/upload/file/webinars/chunk'
    const params = new URLSearchParams()
    if (uploadType === 'course' && courseName) {
      params.append('courseName', courseName)
    }

    const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint

    const { data } = await this.api.post<{ progress: number }>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return data
  }

  uploadImage = async ({ image, options }: { image: File; options?: AxiosRequestConfig }) => {
    const formData = new FormData()
    formData.append('file', image)
    const { data } = await this.api.post<{ url: string }>('/upload/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...options,
    })
    return data
  }

  uploadBlogFile = async ({ file, options }: { file: File; options?: AxiosRequestConfig }) => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await this.api.post<{ s3Key: string; fileName: string; url: string }>(
      '/blog/upload-content',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        ...options,
      }
    )
    return data
  }

  uploadImageBlogs = async ({ image, options }: { image: File; options?: AxiosRequestConfig }) => {
    const formData = new FormData()
    formData.append('file', image)
    const { data } = await this.api.post<{ url: string }>('/upload/file/blogs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...options,
    })
    return data
  }

  uploadVideo = async ({
    video,
    uploadType = 'webinars',
    duration,
    courseName,
    contentHash,
    onProgress,
    signal,
    onInitialized,
  }: {
    video: File
    uploadType?: 'webinars' | 'course'
    duration?: number
    courseName?: string
    contentHash?: string
    options?: AxiosRequestConfig
    onProgress?: (progress: number) => void
    signal?: AbortSignal
    onInitialized?: (payload: { uploadId: string; videoId?: string; uploadLink?: string }) => void
  }) => {
    try {
      // Client → Vimeo TUS via init uploadLink (course + webinars)
      return await this.uploadVideoChunked({
        video,
        uploadType,
        duration,
        courseName,
        contentHash,
        onProgress: onProgress ?? (() => undefined),
        signal,
        onInitialized,
      })
    } catch (error: unknown) {
      throwIfVideoUploadDuplicate(error)
    }
  }

  uploadSandboxZip = async ({ zip, options }: { zip: File; options?: AxiosRequestConfig }) => {
    const formData = new FormData()
    formData.append('file', zip)
    try {
      const { data } = await this.api.post<{ url: string }>('/upload/file/sandbox', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        ...options,
      })
      return data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(error.response.data.message || 'Zip upload failed')
    }
  }

  uploadImageComment = async ({ image, options }: { image: File; options?: AxiosRequestConfig }) => {
    const formData = new FormData()
    formData.append('file', image)
    const { data } = await this.api.post<{ url: string }>('/upload/file/comment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...options,
    })
    return data
  }

  uploadContentAudio = async ({ audio, options }: { audio: File; options?: AxiosRequestConfig }) => {
    const formData = new FormData()
    formData.append('file', audio)
    const { data } = await this.api.post<{ url: string }>('/upload/file/content-audio', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...options,
    })
    return data
  }

  uploadResource = async ({ file, options }: { file: File; options?: AxiosRequestConfig }) => {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const { data } = await this.api.post<{ url: string; s3Key: string; fileName: string; checksum: string }>(
        '/upload/file/resource',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          ...options,
        }
      )
      return data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Resource upload failed')
    }
  }

  uploadHomework = async (formData: FormData, options?: AxiosRequestConfig) => {
    try {
      const { data } = await this.api.post<{
        homeworkFile?: {
          url: string
          s3Key: string
          fileName: string
          checksum: string
        }
        unitTestFiles?: Array<{
          url: string
          s3Key: string
          fileName: string
          checksum: string
        }>
      }>('/upload/file/homework', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        ...options,
      })
      return data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Homework upload failed')
    }
  }

  deleteVideo = async (videoId: string) => {
    const { data } = await this.api.delete(`/upload/video/${videoId}`)
    return data
  }

  getVideoStatus = async (
    videoId: string
  ): Promise<{
    videoId: string
    status: 'processing' | 'ready' | 'error'
    isPlayable: boolean
    transcodeStatus: string | null
  }> => {
    const { data } = await this.api.get(`/upload/video/${videoId}/status`)
    return data
  }
}

const uploadService = UploadService.getInstance(baseService)

export default uploadService
